import type { UIStructure } from "../types";

export const en: UIStructure = {
  onboarding: {
    name: {
      title: "Let's get to know you. What is your first name?",
      description: "So we know how to address you!",
      button: "Continue",
    },
    zipcode: {
      title: "Hi {{name}}, What is your zip code?",
      description:
        "We ask for zip so we can find what's available in your area.",
      button: "Continue",
    },
    email: {
      title: "Great! What's your email address?",
      description:
        "We will use your email to send you important updates and information about your benefits eligibility.",
      button: "Continue",
    },
    partnerMember: {
      title: "Are you currently a member of {{partnerName}}?",
      optIn:
        "Would you be interested in other ways {{partnerName}} can support you in your financial journey?",
      switcher: "Yes, I would be interested",
      button: "Continue",
    },
    householdSize: {
      title: "How many people live in your home?",
      description:
        "Your household size includes your spouse, and children or dependents who live with you, plus yourself!",
      placeholder: "Enter the number of people in your home",
      button: "Continue",
    },
    householdIncome: {
      title: "What is your annual household income before taxes?",
      description: "Your best guess is fine!",
      placeholder: "Enter number",
      suffix: "per year",
      button: "Continue",
    },
    householdAges: {
      title: "How old are the members of your household?",
      description:
        "Asking for age helps us know what resources your family may qualify for.",
      label: "How old is household member #{{index}}?",
      placeholder: "Age of household member #{{index}}",
      button: "Continue",
    },
    preferences: {
      title: "What areas are you looking to get assistance in?",
      description:
        "This helps us understand what benefits you are most interested in.",
      interestedProgramTypesLabel: "Select all that apply",
      freeformResponseLabel:
        "Or describe your situation and our specialists will follow up with tailored resources for you:",
      button: "Continue",
    },
  },
  landingPage: {
    mailingListForm: {
      title:
        "{{partnerName}} is here to empower you in your financial journey.",
      description: "To stay connected, enter your email",
      button: "continue",
      successMessage: "Thank you for signing up!",
    },
    howItWorks: {
      title: "How it works",
      step1: {
        title: "Check Eligibility",
        description:
          "Easily determine your eligibility for benefits and how much you can save.",
      },
      step2: {
        title: "Get Personalized Guidance",
        description: "Get step-by-step guidance on how to apply.",
      },
      step3: {
        title: "Continue Your Financial Journey",
        description: "Get support in the next step of your financial future.",
      },
    },
    zipCodeForm: {
      placeholder: "Enter Zip Code",
      button: "Get Started",
    },
    hero: {
      title: "Welfare Access with {{partnerName}}",
      subtitle: "A benefits finder to help you",
      subtitleHilight: "save",
      callToAction: "See how you can",
      callToActionHighlight:
        "save on healthcare, groceries, utilities, childcare, and more.",
    },
  },
  about: {
    title: "About {{partnerName}}",
  },
  general: {
    copyright: " Starlight. All rights reserved.",
    navigation: {
      label: "Main Menu",
      hub: "Benefits Hub",
      profile: "My Profile",
      faq: "FAQ",
      about: "About",
      signOut: "Sign Out",
    },
    privacyPolicy: "Privacy Policy",
    poweredBy: "Powered by",
    terms: "Terms and Conditions",
    back: "Back",
    next: "Next",
    continue: "Continue",
    backToHub: "Back To Hub",
    yes: "Yes",
    no: "No",
    header: {
      login: "Already have an account?",
      button: "Log in",
    },
  },
  hub: {
    savingsCallOut: "Applying for the benefits below could get you up to",
    savingsCallOutFrequency: "monthly",
    questionsButton: "Questions? Talk with a benefit specialist",
    title: "Benefits For You",
    subtitle:
      "Based on your information so far, we found the following benefits.",
    titleUnavailable: "Coming Soon to Your Area!",
    subtitleUnavailable:
      "We're not in your area yet, but we're happy to help in the meantime!",
    descriptionUnavailable:
      "Our team can manually assist you with any questions or guidance you need. Simply contact us via the chat support button below.",
  },
  auth: {
    signIn: {
      title: "Your Financial Safety Net",
      subtitle: "Let's get started.",
      description:
        "We are here to help you access benefits programs for healthcare, groceries, utilities, and more.",
      callToAction: "Need to create an account?",
      signUp: "Sign up",
      footerText:
        "You will receive a text message to verify your phone number. Your privacy is our top priority. Learn about our",
      error: {
        title: "Error in sign in",
        description: "Please go back to the",
        previous: "previous page",
        description2: "and try again",
      },
      phoneVerify: {
        title: "Please enter the code sent to {{phone}}",
        button: "Verify",
      },
    },
    signUp: {
      titleZipCode: "Welcome to {{partnerName}}'s Benefits Hub",
      titleBenefits: "We found ",
      titleBenefitsHighlight: "{{programCount}} benefits ",
      titleBenefits2: "in your area that could help you save.",
      titleUnavailable: "We are not yet available in your area.",
      descriptionBenefits:
        "Sign up below and start making these savings a reality.",
      descriptionUnavailable:
        "You can still sign up below and we will notify you when benefits become available.",
      disclaimer: "By signing up you agree to our",
      andThe: "and the",
      phoneVerify: {
        title: "Enter your phone number",
        description: "We'll text you a 6-digit PIN to ensure security.",
        button: "Sign Up",
      },
    },
  },
  programs: {
    successQuestion: "Were you able to apply successfully? Let us know!",
    title: "Start your application for {{programName}}",
    description: "We'll guide you through the following steps.",
    step: "step",
    program: {
      title: "{{programName}}",
      cardTitle: "Want a more accurate estimate?",
      cardDescription:
        "Answer a few more questions to see if you may qualify for more!",
      applyButton: "Learn how to apply",
      cardSavings: "Your estimated savings: up to {{savings}}/month",
      cardNonEligible:
        "You may not be eligible, but you can still learn more about this program",
    },
    savings: {
      titleSuccess: "You could be eligible for {{savings}} per month!",
      titleFail: "You may not be eligible for this program.",
      descriptionSuccess:
        "Based on your information, such as income and family size, you could receive up to {{savings}} per month.",
      descriptionFail:
        "Based on your information, such as income and family size, you may not be eligible for this program, but you can still look at our guidence!",
      howApply: "See how to apply",
    },
  },
  requiredFields: {
    title: "A few questions to see how much you could save...",
    adultHealthPremium: {
      label: "How much do you pay for health insurance each month?",
    },
    annualIncome: {
      label: "What is your annual income?",
    },
    cancer: {
      label: "Do you have cancer?",
    },
    childCareCost: {
      label: "What is your monthly child care cost?",
    },
    childCareHours: {
      label: "How many hours of child care do you need per week?",
    },
    childHealthPremium: {
      label:
        "How much do you pay for your child's health insurance each month?",
    },
    childSupportExpense: {
      label: "How much do you pay for child support each month?",
    },
    childSupportIncome: {
      label: "How much child support income do you receive each month?",
    },
    ssdiAmount: {
      label: "What is your monthly SSDI amount?",
    },
    employerSubsidizedHealthcare: {
      label: "Do you receive employer-subsidized healthcare?",
    },
    feedFamilyCost: {
      label: "What is your monthly cost to feed your family?",
    },
    filingJointly: {
      label: "Are you filing jointly?",
    },
    foodStamps: {
      label: "How much do you receive in SNAP/food stamps each month?",
    },
    fullTimeStudent: {
      label: "Are you a full-time student?",
    },
    giIncome: {
      label: "What is your GI income?",
    },
    grReceiving: {
      label: "Are you receiving GR?",
    },
    homeless: {
      label: "Are you currently living without a permanent address?",
    },
    housingCosts: {
      label: "What are your monthly housing costs?",
    },
    kinshipFamily: {
      label: "Are you part of a kinship family?",
    },
    lifeline: {
      label: "Do you receive lifeline?",
    },
    livingApart: {
      label: "Are you living apart from your spouse?",
    },
    mainHouseChild: {
      label: "Do you have a child in the main house?",
    },
    medicaid: {
      label: "Are you on Medicaid?",
    },
    married: {
      label: "Are you married?",
    },
    ssiChildrenNumber: {
      label: "How many children do you have on SSI?",
    },
    otherIncome: {
      label: " How much other income do you receive each month?",
    },
    pregnant: {
      label: "Are you pregnant?",
    },
    schoolLunch: {
      label: "Do your children receive free or reduced-price school lunch?",
    },
    ssiSpouseAmount: {
      label: "What is your spouse's SSI amount?",
    },
    ssiYouAmount: {
      label: "What is your SSI amount?",
    },
    ssiKidsAmount: {
      label: "What is your children's SSI amount?",
    },
    ssiOver65: {
      label: "Are you 65 or older and on SSI?",
    },
    studentSnapExemptions: {
      label: "Do you have any student SNAP exemptions?",
    },
    unemploymentAmount: {
      label: "What is your unemployment benefit amount?",
    },
    unemployed: {
      label: "Are you currently unemployed?",
    },
    utilitiesInName: {
      label: "Are the utilities in your name?",
    },
    welfareAmount: {
      label: "What is your welfare amount?",
    },
    workStudyAmount: {
      label: "Do you earn anything as a work-study student?",
    },
    downPaymentContributionAmount: {
      label: "How much can you contribute to a down payment?",
    },
    isFirstTimeHomeBuyer: {
      label:
        "Are you a first-time homebuyer? (This means you havent owned a home in at least three years)",
    },
    ownerOccupied: {
      label: "Will you be living in the home you buy?",
    },
  },
  benefits: {
    availableBenefits: "Available Benefits",
    food: {
      title: "Food",
      description:
        "Discover eligibility for SNAP, WIC, and local food pantries.",
    },
    healthcare: {
      title: "Healthcare",
      description: "Find out if you qualify for Medicaid and ACA plans.",
    },
    housing: {
      title: "Housing",
      description: "Get help with rent and down payment assistance programs.",
    },
    utilities: {
      title: "Utilities",
      description:
        "Check eligibility for HEAP and utility bill assistance programs.",
    },
    childcare: {
      title: "Childcare",
      description: "Access childcare assistance and early education programs.",
    },
    taxCredits: {
      title: "Tax Credits",
      description:
        "See if you qualify for EITC, Child Tax Credit, and other tax benefits.",
    },
    jobSupport: {
      title: "Job Support",
      description: "Learn about unemployment insurance.",
    },
    otherSupport: {
      title: "Other Support",
      description: "Find senior services, medical bill assistance, and more.",
    },
  },
};
